"use client"

import type React from "react"

import { useState, useEffect } from "react"
import type { MigrationFormData } from "../types/migrations"

interface UseMigrationFormResult {
  formData: MigrationFormData
  formErrors: Partial<MigrationFormData>
  isFormValid: boolean
  formSubmitted: boolean
  file: File | null
  fileUploadStatus: "idle" | "uploading" | "success" | "error"
  uploadProgress: number
  setFormSubmitted: (value: boolean) => void
  handleChange: (e: React.ChangeEvent<{ name?: string; value: unknown }> | any) => void
  handleFileUpload: (event: React.DragEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>) => void
  handleRemoveFile: () => void
  validateForm: () => boolean
}

/**
 * Custom hook for managing migration form state and validation
 */
export function useMigrationForm(): UseMigrationFormResult {
  const [formData, setFormData] = useState<MigrationFormData>({
    projectName: "",
    llmModel: "gemini",
    sourceSystem: "ibm",
    destinationSystem: "APIGEE",
  })
  const [formErrors, setFormErrors] = useState<Partial<MigrationFormData>>({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [fileUploadStatus, setFileUploadStatus] = useState<"idle" | "uploading" | "success" | "error">("idle")
  const [uploadProgress, setUploadProgress] = useState(0)

  useEffect(() => {
    validateForm()
  }, [formData, file, fileUploadStatus])

  const validateForm = () => {
    const errors: Partial<MigrationFormData> = {}
    let isValid = true

    if (!formData.projectName.trim()) {
      errors.projectName = "Migration name is required"
      isValid = false
    }
    if (!formData.llmModel) {
      errors.llmModel = "LLM model is required"
      isValid = false
    }
    if (!formData.sourceSystem) {
      errors.sourceSystem = "Source system is required"
      isValid = false
    }
    if (!formData.destinationSystem) {
      errors.destinationSystem = "Destination system is required"
      isValid = false
    }

    setFormErrors(errors)
    setIsFormValid(isValid && file !== null && fileUploadStatus === "success")
    return isValid && file !== null && fileUploadStatus === "success"
  }

  const handleChange = (e: React.ChangeEvent<{ name?: string; value: unknown }> | any) => {
    const name = e.target?.name as keyof MigrationFormData
    const value = e.target.value as string
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileUpload = (event: React.DragEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    let selectedFile: File | null = null

    if ("dataTransfer" in event) {
      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        selectedFile = event.dataTransfer.files[0]
      }
    } else if ("target" in event && event.target.files && event.target.files.length > 0) {
      selectedFile = event.target.files[0]
    }

    if (selectedFile) {
      setFileUploadStatus("uploading")
      setFile(selectedFile)

      let progress = 0
      const interval = setInterval(() => {
        progress += 10
        setUploadProgress(progress)
        if (progress >= 100) {
          clearInterval(interval)
          setFileUploadStatus("success")
        }
      }, 300)
    }
  }

  const handleRemoveFile = () => {
    setFile(null)
    setFileUploadStatus("idle")
    setUploadProgress(0)
  }

  return {
    formData,
    formErrors,
    isFormValid,
    formSubmitted,
    file,
    fileUploadStatus,
    uploadProgress,
    setFormSubmitted,
    handleChange,
    handleFileUpload,
    handleRemoveFile,
    validateForm,
  }
}
